import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SELLER_NAME_MAX, SELLER_NOTE_MAX, sellerProfileUrl } from '@seller/shared';
import { api, ApiError } from '../api/client.js';
import { sellerAliasBody } from '../lib/sellers.js';
import { Button, Input } from './ui.js';

/**
 * Competitor sellers, as the offers card and the Known sellers page show them.
 *
 * Amazon's Pricing API names a seller only by an opaque id and the app never
 * scrapes Amazon's pages, so every seller id is a link to Amazon's own public
 * seller profile page (opened in a new tab), and the owner may give the id a name
 * of their own. See docs/offers.md, "Naming competitors".
 */

/** The query keys a seller name shows under, refreshed after every name change. */
export function invalidateSellerNames(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['sellers'] });
  void queryClient.invalidateQueries({ queryKey: ['offers'] });
  void queryClient.invalidateQueries({ queryKey: ['offers-summary'] });
}

/**
 * One seller: "Name (id)" when the owner named it, "You (id)" for our own seller,
 * else the bare id — linked to the seller's public Amazon profile.
 */
export function SellerLink({
  sellerId,
  name,
  isOurs = false,
  marketplaceId,
}: {
  sellerId: string;
  name: string | null;
  isOurs?: boolean;
  marketplaceId: string;
}) {
  const label = isOurs ? 'You' : name;
  return (
    <a
      href={sellerProfileUrl(sellerId, marketplaceId)}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:underline"
      title="Open this seller's public profile on Amazon (new tab)"
    >
      {label ? (
        <>
          <span className="font-medium text-body">{label}</span>{' '}
          <span className="font-mono text-xs text-muted">({sellerId})</span>
        </>
      ) : (
        <span className="font-mono text-xs text-brand-600">{sellerId}</span>
      )}
    </a>
  );
}

/** A seller's stored name and note, as `GET /api/sellers/:sellerId` answers. */
interface StoredAlias {
  sellerId: string;
  name: string;
  note: string | null;
}

/**
 * The small name-and-note form that names (or renames) a seller. It opens on the
 * name and note stored NOW (a fresh `GET /api/sellers/:sellerId`, not what the page
 * loaded earlier), so naming a seller by id that already has a name edits that
 * name. Saving upserts `PUT /api/sellers/:sellerId` — with the note only when the
 * owner changed it here (lib/sellers.ts) — and refreshes every view of the name.
 */
export function SellerNameForm({ sellerId, onDone }: { sellerId: string; onDone: () => void }) {
  const stored = useQuery({
    queryKey: ['sellers', 'one', sellerId],
    queryFn: async (): Promise<StoredAlias | null> => {
      try {
        return await api.get<StoredAlias>(`/api/sellers/${encodeURIComponent(sellerId)}`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    // Always ask the server when the form opens; a cached answer could be stale.
    staleTime: 0,
    gcTime: 0,
  });

  if (stored.isPending) return <p className="mt-2 text-xs text-muted">Loading the stored name…</p>;
  if (stored.isError) {
    return (
      <p className="mt-2 text-xs text-danger-text" role="alert">
        {stored.error.message}
      </p>
    );
  }
  return (
    <NameFormFields
      sellerId={sellerId}
      initialName={stored.data?.name ?? ''}
      initialNote={stored.data?.note ?? ''}
      onDone={onDone}
    />
  );
}

function NameFormFields({
  sellerId,
  initialName,
  initialNote,
  onDone,
}: {
  sellerId: string;
  initialName: string;
  initialNote: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [note, setNote] = useState(initialNote);
  const [noteTouched, setNoteTouched] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.put(
        `/api/sellers/${encodeURIComponent(sellerId)}`,
        sellerAliasBody(name, note, noteTouched),
      ),
    onSuccess: () => {
      invalidateSellerNames(queryClient);
      onDone();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (name.trim()) save.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-2 flex flex-wrap items-end gap-2"
      aria-label={`Name seller ${sellerId}`}
    >
      <label className="block min-w-40 flex-1">
        <span className="mb-1 block text-xs text-muted">Name</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={SELLER_NAME_MAX}
          placeholder="Your name for this seller"
          required
          autoFocus
        />
      </label>
      <label className="block min-w-40 flex-[2]">
        <span className="mb-1 block text-xs text-muted">Note (optional)</span>
        <Input
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setNoteTouched(true);
          }}
          maxLength={SELLER_NOTE_MAX}
          placeholder="Anything worth remembering"
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {save.isError && (
        <p className="w-full text-xs text-danger-text" role="alert">
          {save.error.message}
        </p>
      )}
    </form>
  );
}
